import type { IntegrationError, IntegrationErrorCode } from '../types';

/**
 * Retry configuration
 */
export interface RetryConfig {
  maxAttempts: number;
  backoffMs: number;
  maxBackoffMs?: number;
  retryableErrors?: IntegrationErrorCode[];
}

/**
 * Execute a function with retry logic
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  config: RetryConfig,
): Promise<T> {
  const {
    maxAttempts,
    backoffMs,
    maxBackoffMs = 30000,
    retryableErrors,
  } = config;

  let lastError: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Check if error is retryable
      if (
        error &&
        typeof error === 'object' &&
        'code' in error &&
        retryableErrors
      ) {
        const integrationError = error as IntegrationError;
        if (!retryableErrors.includes(integrationError.code)) {
          throw error;
        }
      }

      // Last attempt, throw
      if (attempt === maxAttempts) {
        throw error;
      }

      // Wait before retry (exponential backoff)
      const delay = Math.min(
        backoffMs * Math.pow(2, attempt - 1),
        maxBackoffMs,
      );
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}
